export interface UniversityProgram {
  slug: string
  title: string
  shortTitle: string
  degreeLevel: string
  focusArea: string
  description: string
  websiteUrl: string
  jobOpportunitiesIntro: string
  jobOpportunities: string[]
  keyFacts: string[]
  curriculum: { course: string; credits: number }[]
}

export interface University {
  slug: string
  name: string
  location: string
  description: string
  accreditation: string
  phone: string
  email: string
  website: string
  logoUrl?: string
  programs: UniversityProgram[]
}

export const universities: University[] = [
  {
    slug: 'global-tech-college',
    name: 'Global Tech College',
    location: 'Toledo, OH',
    description:
      "Global Tech College offers career-focused degree and certificate programs designed to prepare students for success in today's competitive job market. With flexible online options and dedicated support, we help you achieve your educational goals.",
    accreditation:
      'The Accrediting Council for Continuing Education & Training (ACCET)',
    phone: '(419) 315-4388',
    email: 'info@globaltech.edu',
    website: 'https://globaltech.edu',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/kMxFqqE7MbeZ2oDSzfEEA9/GTC_LOGO_(1).webp',
    programs: [
      {
        slug: 'computer-science-diploma',
        title: 'Diploma in Computer Science',
        shortTitle: 'CS Diploma',
        degreeLevel: '1', // Certificate
        focusArea: '4', // Computers & IT
        websiteUrl: 'https://globaltech.edu/computer-science-diploma/',
        description:
          "The Computer Science Diploma Program is designed to prepare students for entry-level positions in the computer programming and computer operations fields, office and administrative support, and starting one's own business. Courses focus on skills that help develop as entrepreneurs in business and technology.",
        jobOpportunitiesIntro: 'Potential job titles include',
        jobOpportunities: [
          'Computer Support Associate',
          'Computer Support',
          'Computer Technician',
          'Desktop Support Technician',
          'Help Desk Technician',
          'Help Desk Associate',
          'Information Technology Specialist (IT Specialist)',
          'Technical Support Specialist',
          'Desktop Support',
          "Starting one's own business",
        ],
        keyFacts: [
          'Program Length: 1.2 years',
          'Total Credits Required: 56',
          'Total Clock Hours: 1,008',
          'Title IV Approved Online Programs',
          'Program Delivery: 2-hour pre-recorded lecture and 2-hour live lecture per week',
          'Student Support: Live Tutor support via email & chat',
        ],
        curriculum: [
          {
            course: 'AI 101 – Introduction to Artificial Intelligence',
            credits: 4,
          },
          {
            course:
              'COM 102 – Business Communication with Artificial Intelligence',
            credits: 4,
          },
          { course: 'CS 101 – Computer Concepts', credits: 4 },
          { course: 'CS 102 – Operating Systems', credits: 4 },
          { course: 'CS 103 – Managing & Maintaining PC 1', credits: 4 },
          { course: 'CS 104 – Managing & Maintaining PC 2', credits: 4 },
          { course: 'CS 106 – HTML & Java Script Basics', credits: 4 },
          { course: 'CS 107 – Computer Support 1', credits: 4 },
          { course: 'CS 108 – Computer Support 2', credits: 4 },
          { course: 'CS 109 – Networking 1', credits: 4 },
          { course: 'CS 110 – Networking 2', credits: 4 },
          { course: 'CS 111 – Programming with Python', credits: 4 },
          { course: 'CS 112 – Microsoft Office with Copilot', credits: 4 },
          {
            course: 'CS 113 – Microsoft Office Advanced with Copilot',
            credits: 4,
          },
        ],
      },
      {
        slug: 'associate-of-applied-science-computer-science',
        title: 'Associate of Applied Science – Computer Science',
        shortTitle: 'AAS Computer Science',
        degreeLevel: '2', // Associates
        focusArea: '4', // Computers & IT
        websiteUrl:
          'https://globaltech.edu/associate-of-applied-science-computer-science/',
        description:
          'The Associate of Applied Science (AAS) – Computer Science is designed to provide students with the skills to prepare for career growth and success in the related fields in computer science. GTC courses help students to prepare for a tech-driven career with hands-on training, industry-relevant knowledge, and a strong entrepreneurial foundation.',
        jobOpportunitiesIntro:
          'Computer User Support Specialists including job titles such as',
        jobOpportunities: [
          'Computer Specialist',
          'Computer Support Specialist',
          'Computer Technician',
          'Desktop Support Technician',
          'Help Desk Analyst',
          'Help Desk Technician',
          'Information Technology Specialist (IT Specialist)',
          'Support Specialist',
          'Technical Support Specialist',
          "Starting one's own business",
        ],
        keyFacts: [
          'Program Length: 2.2 years',
          'Total Credits Required: 96',
          'Total Clock Hours: 1,728',
          'Title IV Approved Online Programs',
          'Program Delivery: 2-hour pre-recorded lecture and 2-hour live lecture per week',
          'Student Support: Live tutor support, via email & chat',
        ],
        curriculum: [
          {
            course: 'AI 101 – Introduction to Artificial Intelligence',
            credits: 4,
          },
          { course: 'BUS 101 – Business Law', credits: 4 },
          { course: 'BUS 104 – Business Math 1', credits: 4 },
          { course: 'BUS 106 – Organizational Behavior', credits: 4 },
          { course: 'BUS 109 – Introduction to Business', credits: 4 },
          { course: 'BUS 115 – Macroeconomics', credits: 4 },
          { course: 'BUS 116 – Microeconomics', credits: 4 },
          {
            course:
              'COM 102 – Business Communication with Artificial Intelligence',
            credits: 4,
          },
          { course: 'CS 101 – Computer Concepts', credits: 4 },
          { course: 'CS 102 – Operating Systems', credits: 4 },
          { course: 'CS 103 – Managing & Maintaining PC 1', credits: 4 },
          { course: 'CS 104 – Managing & Maintaining PC 2', credits: 4 },
          { course: 'CS 106 – HTML & Java Script Basics', credits: 4 },
          { course: 'CS 107 – Computer Support 1', credits: 4 },
          { course: 'CS 108 – Computer Support 2', credits: 4 },
          { course: 'CS 109 – Networking 1', credits: 4 },
          { course: 'CS 110 – Networking 2', credits: 4 },
          { course: 'CS 111 – Programming with Python', credits: 4 },
          { course: 'CS 112 – Microsoft Office with Copilot', credits: 4 },
          {
            course: 'CS 113 – Microsoft Office Advanced with Copilot',
            credits: 4,
          },
          { course: 'CS 114 – Information Assurance', credits: 4 },
          { course: 'ENG 101 – English Composition 1', credits: 4 },
          { course: 'ENG 102 – English Composition 2', credits: 4 },
          { course: 'HIS 101 – History of Western Civilization', credits: 4 },
        ],
      },
      {
        slug: 'business-management-diploma',
        title: 'Diploma in Business Management',
        shortTitle: 'Business Diploma',
        degreeLevel: '1', // Certificate
        focusArea: '1', // Business & Finance
        websiteUrl: 'https://globaltech.edu/business-management-diploma/',
        description:
          'The Business Management Diploma Program is designed to prepare students for entry-level management positions. Courses focus on skills that help students develop as entrepreneurs in the business field.',
        jobOpportunitiesIntro: 'Potential job titles include',
        jobOpportunities: [
          'Accounting Associate',
          'Accounts Payable Clerk',
          'Accounts Receivable Clerk',
          'Administrative Support',
          'Customer Service Agent',
          'Customer Service Agent',
          'Office Clerk',
          'Office Support Specialist',
          'Office Support',
          'Staff Services Specialist',
          "Starting one's own business",
        ],
        keyFacts: [
          'Program Length: 1.2 years',
          'Total Credits Required: 56',
          'Total Clock Hours: 1,008',
          'Title IV Approved Online Programs',
          'Program Delivery: 2-hour pre-recorded lecture and 2-hour live lecture per week',
          'Student Support: Live tutor support, via email & chat',
        ],
        curriculum: [
          {
            course: 'AI 101 – Introduction to Artificial Intelligence',
            credits: 4,
          },
          { course: 'ACC 101 – Accounting I', credits: 4 },
          { course: 'BUS 101 – Business Law', credits: 4 },
          { course: 'BUS 103 – Banking Operations', credits: 4 },
          { course: 'BUS 104 – Business Math 1', credits: 4 },
          { course: 'BUS 106 – Organizational Behavior', credits: 4 },
          {
            course: 'BUS 108 – Operations and Supply Chain Management',
            credits: 4,
          },
          { course: 'BUS 109 – Introduction to Business', credits: 4 },
          { course: 'BUS 110 – Project Management', credits: 4 },
          { course: 'BUS 111 – Total Quality Management', credits: 4 },
          { course: 'BUS 112 – Small Business Management', credits: 4 },
          { course: 'BUS 114 – Human Resource Management', credits: 4 },
          {
            course:
              'COM 102 – Business Communication with Artificial Intelligence',
            credits: 4,
          },
          { course: 'CS 112 – Microsoft Office with Copilot', credits: 4 },
        ],
      },
      {
        slug: 'associate-of-applied-business-business-management',
        title: 'Associate of Applied Business – Business Management',
        shortTitle: 'AAB Business Management',
        degreeLevel: '2', // Associates
        focusArea: '1', // Business & Finance
        websiteUrl:
          'https://globaltech.edu/associate-of-applied-business-business-management/',
        description:
          'The Associate of Applied Business (AAB) – Business Management is designed to provide students with the skills to prepare them for career growth and success in business management-related fields. Course is designed to foster entrepreneurial skills, including creative problem solving, effective communication, and independent thinking.',
        jobOpportunitiesIntro: 'Potential job titles include',
        jobOpportunities: [
          'Accounting Assistant Supervisor',
          'Accounts Payable Coordinator',
          'Accounts Receivable Coordinator',
          'Administrative Coordinator',
          'Customer Service Supervisor',
          'Customer Service Supervisor',
          'Office Coordinator',
          'Office Supervisor',
          'Staff Services Coordinator',
          'Staff Services Supervisor',
          "Starting one's own business",
        ],
        keyFacts: [
          'Program Length: 2.2 years',
          'Total Credits Required: 96',
          'Total Clock Hours: 1,728',
          'Title IV Approved Online Programs',
          'Program Delivery: 2-hour pre-recorded lecture and 2-hour live lecture per week',
          'Student Support: Live tutor support, via email & chat',
        ],
        curriculum: [
          {
            course: 'AI 101 – Introduction to Artificial Intelligence',
            credits: 4,
          },
          { course: 'ACC 101 – Accounting I', credits: 4 },
          { course: 'ACC 104 – Payroll Accounting', credits: 4 },
          { course: 'BUS 101 – Business Law', credits: 4 },
          { course: 'BUS 103 – Banking Operations', credits: 4 },
          { course: 'BUS 104 – Business Math 1', credits: 4 },
          { course: 'BUS 106 – Organizational Behavior', credits: 4 },
          { course: 'BUS 107 – Principles of Marketing', credits: 4 },
          {
            course: 'BUS 108 – Operations and Supply Chain Management',
            credits: 4,
          },
          { course: 'BUS 109 – Introduction to Business', credits: 4 },
          { course: 'BUS 110 – Project Management', credits: 4 },
          { course: 'BUS 111 – Total Quality Management', credits: 4 },
          { course: 'BUS 112 – Small Business Management', credits: 4 },
          { course: 'BUS 113 – International Business', credits: 4 },
          { course: 'BUS 114 – Human Resource Management', credits: 4 },
          { course: 'BUS 115 – Macroeconomics', credits: 4 },
          { course: 'BUS 116 – Microeconomics', credits: 4 },
          {
            course:
              'COM 102 – Business Communication with Artificial Intelligence',
            credits: 4,
          },
          { course: 'CS 101 – Computer Concepts', credits: 4 },
          { course: 'CS 112 – Microsoft Office with Copilot', credits: 4 },
          {
            course: 'CS 113 – Microsoft Office Advanced with Copilot',
            credits: 4,
          },
          { course: 'ENG 101 – English Composition 1', credits: 4 },
          { course: 'ENG 102 – English Composition 2', credits: 4 },
          { course: 'HIS 101 – History of Western Civilization', credits: 4 },
        ],
      },
    ],
  },
  {
    slug: 'ultimate-medical-academy',
    name: 'Ultimate Medical Academy',
    location: 'Tampa, FL',
    description:
      'UMA students get personalized attention throughout the entire education journey. Our students get support from the very beginning, throughout career training, and after graduation.',
    accreditation: 'Accrediting Bureau of Health Education Schools (ABHES)',
    phone: '888-266-7773',
    email: 'info@ultimatemedical.edu',
    website: 'https://www.ultimatemedical.edu',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/ftg2brgTZFks7CVCNZ5jT1/UMA_LOGO_(1).webp',
    programs: [
      {
        slug: 'health-and-human-services',
        title: 'Health and Human Services',
        shortTitle: 'Health & Human Services',
        degreeLevel: '2',
        focusArea: '2',
        websiteUrl:
          'https://www.ultimatemedical.edu/programs/health-and-human-services/',
        description:
          'Prepare for a rewarding career helping others navigate the healthcare system and access essential services.',
        jobOpportunitiesIntro: 'Potential job titles include',
        jobOpportunities: [
          'Community Health Worker',
          'Social and Human Service Assistant',
          'Health Educator',
        ],
        keyFacts: ['Flexible online classes', 'Dedicated student support'],
        curriculum: [],
      },
      {
        slug: 'health-information-management',
        title: 'Health Information Management',
        shortTitle: 'Health Info Management',
        degreeLevel: '2',
        focusArea: '2',
        websiteUrl:
          'https://www.ultimatemedical.edu/programs/health-information-management/',
        description:
          'Learn to manage patient health information and medical records, ensuring accuracy, accessibility, and security.',
        jobOpportunitiesIntro: 'Potential job titles include',
        jobOpportunities: [
          'Health Information Technician',
          'Medical Records Specialist',
        ],
        keyFacts: ['Flexible online classes', 'Dedicated student support'],
        curriculum: [],
      },
      {
        slug: 'healthcare-accounting',
        title: 'Healthcare Accounting',
        shortTitle: 'Healthcare Accounting',
        degreeLevel: '2',
        focusArea: '2',
        websiteUrl:
          'https://www.ultimatemedical.edu/programs/healthcare-accounting/',
        description:
          'Gain the skills needed to manage financial records and processes specifically within healthcare organizations.',
        jobOpportunitiesIntro: 'Potential job titles include',
        jobOpportunities: ['Accounting Clerk', 'Billing Coordinator'],
        keyFacts: ['Flexible online classes', 'Dedicated student support'],
        curriculum: [],
      },
      {
        slug: 'healthcare-management',
        title: 'Healthcare Management',
        shortTitle: 'Healthcare Management',
        degreeLevel: '2',
        focusArea: '2',
        websiteUrl:
          'https://www.ultimatemedical.edu/programs/healthcare-management/',
        description:
          'Develop leadership and management skills tailored for the dynamic healthcare industry.',
        jobOpportunitiesIntro: 'Potential job titles include',
        jobOpportunities: [
          'Medical and Health Services Manager',
          'Clinical Director',
        ],
        keyFacts: ['Flexible online classes', 'Dedicated student support'],
        curriculum: [],
      },
      {
        slug: 'medical-administrative-assistant',
        title: 'Medical Administrative Assistant',
        shortTitle: 'Medical Admin Assistant',
        degreeLevel: '1',
        focusArea: '2',
        websiteUrl:
          'https://www.ultimatemedical.edu/programs/medical-administrative-assistant/',
        description:
          'Train to handle the administrative duties that keep medical offices running smoothly.',
        jobOpportunitiesIntro: 'Potential job titles include',
        jobOpportunities: ['Medical Secretary', 'Administrative Assistant'],
        keyFacts: ['Flexible online classes', 'Dedicated student support'],
        curriculum: [],
      },
      {
        slug: 'medical-billing-and-coding',
        title: 'Medical Billing and Coding',
        shortTitle: 'Medical Billing & Coding',
        degreeLevel: '1',
        focusArea: '2',
        websiteUrl:
          'https://www.ultimatemedical.edu/programs/medical-billing-and-coding/',
        description:
          'Learn to translate healthcare services into standardized codes for billing and insurance purposes.',
        jobOpportunitiesIntro: 'Potential job titles include',
        jobOpportunities: ['Medical Biller', 'Medical Coder'],
        keyFacts: ['Flexible online classes', 'Dedicated student support'],
        curriculum: [],
      },
      {
        slug: 'medical-office-and-billing-specialist',
        title: 'Medical Office and Billing Specialist',
        shortTitle: 'Medical Office & Billing',
        degreeLevel: '1',
        focusArea: '2',
        websiteUrl:
          'https://www.ultimatemedical.edu/programs/medical-office-and-billing-specialist/',
        description:
          'Combine administrative skills with billing expertise to become a versatile healthcare office professional.',
        jobOpportunitiesIntro: 'Potential job titles include',
        jobOpportunities: ['Medical Office Assistant', 'Billing Specialist'],
        keyFacts: ['Flexible online classes', 'Dedicated student support'],
        curriculum: [],
      },
    ],
  },
]

export function getUniversityBySlug(slug: string): University | undefined {
  return universities.find((u) => u.slug === slug)
}

export function getProgramBySlug(
  university: University,
  programSlug: string,
): UniversityProgram | undefined {
  return university.programs.find((p) => p.slug === programSlug)
}
